import { ButtonBase, LinkBase, StackBase, TypographyBase } from '@fmc-digital/ui';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { logOutAction } from '@store/actions/logout.actions';
import { selectUserInfo, selectUserProfile } from '@store/selectors/widgets.selectors';
import { useQueryClient } from '@tanstack/react-query';
import { MainMenuOptions, NavigationOptions } from '@types';
import { clearCookie } from '@utils/cookieHelper';
import { getIntials } from '@utils/getIntials';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth, config } from '../../../../config';
import { useHomeContext } from '../../../context/MyHomeContext';
import { Profile } from '../profile/Profile';
import { MessageUnreadIcon } from '../icons';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { RootState } from '@store';
import { selectAllowedPortcodesForMessageCentre, selectFeatureFlag } from '@store/reducers/config.reducer';
import { logUserEvent } from '@analytics/amplifyLogging';
import { useTheme } from '@mui/material';
import { filterDisableMenuOption } from '@utils/disableMenuOptions';

export const MenuButton = ({
    id,
    name,
    subMenuOptions: options,
    isServicing = false,
    selectedLoanNumber,
    onHeaderClickHandler
}: MainMenuOptions) => {
    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef<HTMLDivElement | null>(null);
    const dispatch = useDispatch();
    const context = useHomeContext();
    const navigate = useNavigate();
    const location = useLocation();
    const unreadCount = useSelector((state: RootState) => state.unreadCount.unreadCount);
    const [showMessagecentre, setShowMessageCentre] = useState(false);
    const portCode = useSelector(selectUserInfo).portCode;
    const mcPortCodes = useSelector(selectAllowedPortcodesForMessageCentre);
    const isMCFeatureFlagEnabled = useSelector((state: RootState) => selectFeatureFlag(state, 'messageCenter'));
    const theme = useTheme();
    const { onLogoutEvent } = useHomeContext();
    const client = useQueryClient();
    const handleToggle = async (name: string) => {
        if (onHeaderClickHandler) onHeaderClickHandler(name);
        if (name === 'Log Out') {
            client.clear();
            client.removeQueries();
            onLogoutEvent();
            dispatch(logOutAction());
            await auth.getOkta().signOut();
            auth.getOkta().tokenManager.clear();
            clearCookie();
        }
        setOpen(prevOpen => !prevOpen);
    };

    const { firstName, lastName } = useSelector(selectUserInfo);
    const userProfile: NavigationOptions = useSelector(selectUserProfile);

    const handleClose = (event: Event | React.SyntheticEvent, option: NavigationOptions | undefined = undefined) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }
        if (option) {
            switch (option.id) {
                case 'user_profile_url':
                    clearCookie();
                    auth.getOkta().tokenManager.clear();
                    window.location.replace(`${userProfile.url}?${window.btoa(config.env('redirectUri'))}`);
                    break;
                case 'hardship_assistance_url':
                    window.open(option.url, '_blank');
                    break;
                case 'Escrow Assessment':
                    break;
                case 'payoff_wire_transfer_url':
                    window.open(option.url, '_blank');
                    break;
                case 'FAQs':
                    window.open(option.url, '_blank');
                    break;
                case 'disaster_assistance_url':
                    logUserEvent({
                        action: 'DisasterAssistanceClicked',
                        type: 'Support',
                        description: 'Disaster Assistance Clicked'
                    });
                    navigate('/disaster-assistance');
                    break;
                case 'manage_account_sso_url':
                    context.accountDetailSSO(`${config.env('myLoanBaseUrl')}/LoanService/SSOPost`).catch(console.log);
                    break;
                case 'account_details_sso_url':
                    context.accountDetailSSO(`${config.env('myLoanBaseUrl')}/LoanService/SSOPost`).catch(console.log);
                    break;
                case 'recurring_payment_sso_url':
                    context
                        .accountDetailSSO(`${config.env('myLoanBaseUrl')}/LoanService/SSOPost?target_menu=recurringPayments`)
                        .catch(console.log);
                    break;
                case 'payment_history_sso_url':
                    context
                        .accountDetailSSO(`${config.env('myLoanBaseUrl')}/LoanService/SSOPost?target_menu=paymentHistory`)
                        .catch(console.log);
                    break;
                case 'all_documents_sso_url':
                    context
                        .accountDetailSSO(`${config.env('myLoanBaseUrl')}/LoanService/SSOPost?target_menu=statements`)
                        .catch(console.log);
                    break;
                case 'pay_now_sso_url':
                    context
                        .accountDetailSSO(`${config.env('myLoanBaseUrl')}/LoanService/SSOPost?target_menu=oneTimePayment`)
                        .catch(console.log);
                    break;
                default:
                    break;
            }
        }

        setOpen(false);
    };

    function handleListKeyDown(event: React.KeyboardEvent) {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpen(false);
        } else if (event.key === 'Escape') {
            setOpen(false);
        }
    }

    const onMCButtonClick = () => {
        logUserEvent({
            type: 'Message Center',
            description: 'Customer clicked Message Center Icon',
            action: 'MessageCenterIconClicked'
        });
        navigate('/mc', { state: { from: location } });
    };
    useEffect(() => {
        if (isMCFeatureFlagEnabled) {
            if (mcPortCodes.length === 0) {
                setShowMessageCentre(true);
            }
            mcPortCodes.map(port => {
                if (String(port) === portCode) {
                    setShowMessageCentre(true);
                }
            });
        }
    }, [isMCFeatureFlagEnabled, mcPortCodes, portCode]);
    if (!isServicing && ['Loan_Information', 'Payment_Center', 'Document', 'Message_Center'].some(_id => _id === id)) return null;
    return (
        <React.Fragment>
            {id === 'Message_Center' ? (
                isMCFeatureFlagEnabled && showMessagecentre ? (
                    <StackBase
                        direction="row"
                        alignItems="center"
                        gap="0.5rem"
                        data-testid="mcMenuElement"
                        id={id}
                        sx={{
                            '&:hover span': {
                                fontWeight: 500,
                                fontFamily: 'Poppins-Medium'
                            },
                            cursor: 'pointer'
                        }}
                        onClick={onMCButtonClick}
                    >
                        {unreadCount > 0 ? (
                            <MessageUnreadIcon
                                sx={{
                                    fontSize: '1.5rem',
                                    color: 'primary.contrastText',
                                    cursor: 'pointer'
                                }}
                            />
                        ) : (
                            <MailOutlineIcon
                                sx={{
                                    fontSize: '1.5rem',
                                    color: 'primary.contrastText',
                                    cursor: 'pointer'
                                }}
                            />
                        )}

                        <TypographyBase color={open ? theme.palette.teal.main : theme.palette.surface.contrastText} variant="paragraph">
                            Messages
                        </TypographyBase>
                    </StackBase>
                ) : null
            ) : (
                <StackBase
                    direction="row"
                    alignItems="center"
                    ref={anchorRef}
                    sx={{
                        '&:hover span': {
                            fontWeight: 500,
                            fontFamily: 'Poppins-Medium'
                        },
                        cursor: 'pointer'
                    }}
                    onClick={async () => await handleToggle(name)}
                    id={id}
                >
                    <TypographyBase
                        color={open ? theme.palette.teal.main : theme.palette.surface.contrastText}
                        variant={open ? 'paragraph-bold' : 'paragraph'}
                        id="composition-button"
                        aria-controls={open ? 'composition-menu' : undefined}
                        aria-expanded={open ? 'true' : undefined}
                        aria-haspopup="true"
                    >
                        {id === 'Profile' ? <Profile name={getIntials(`${firstName} ${lastName}`)} open={open} /> : name}
                    </TypographyBase>
                    {Array.isArray(options) && options.length && id !== 'Profile' ? (
                        open ? (
                            <ExpandLessIcon sx={_t => ({ color: _t.palette.teal.main, width: '1.5rem', height: '1.5rem' })} />
                        ) : (
                            <ExpandMoreIcon sx={_t => ({ color: _t.palette.teal.main, width: '1.5rem', height: '1.5rem' })} />
                        )
                    ) : null}
                </StackBase>
            )}

            <Popper
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                placement="bottom"
                transition
                disablePortal
                sx={{ zIndex: 1 }}
            >
                {Array.isArray(options) && options.length
                    ? ({ TransitionProps, placement }) => (
                          <Grow
                              {...TransitionProps}
                              style={{
                                  transformOrigin: 'left'
                              }}
                          >
                              <Paper
                                  sx={{
                                      background: '#1D3146',
                                      color: '#6ED8FF',
                                      minWidth: '26.5rem',
                                      maxWidth: '26.5rem',
                                      marginTop: '1.5rem',
                                      padding: '2rem',
                                      '&.MuiPaper-root.MuiPaper-rounded': {
                                          borderRadius: '0',
                                          borderBottomRightRadius: '0.5rem',
                                          borderBottomLeftRadius: '0.5rem'
                                      }
                                  }}
                              >
                                  <ClickAwayListener onClickAway={event => handleClose(event)}>
                                      <MenuList
                                          autoFocusItem={false}
                                          id="composition-menu"
                                          aria-labelledby="composition-button"
                                          onKeyDown={handleListKeyDown}
                                          disablePadding={true}
                                          sx={{
                                              '& li': {
                                                  padding: '0.5rem 1rem'
                                              },
                                              '& li:hover': {
                                                  backgroundColor: 'unset'
                                              },
                                              '& li:not(:last-child)': {
                                                  marginBottom: '0.5rem'
                                              }
                                          }}
                                      >
                                          {options.map((option: NavigationOptions, idx) => {
                                              return (
                                                  <MenuItem
                                                      onClick={event => handleClose(event, option)}
                                                      key={`${idx}_menu_item`}
                                                      disabled={filterDisableMenuOption(option, selectedLoanNumber)}
                                                      sx={{
                                                          '&.Mui-disabled': {
                                                              userSelect: 'text'
                                                          }
                                                      }}
                                                  >
                                                      <ListItemText key={`${idx}_list_item`}>
                                                          {option.id === 'call_at' ? (
                                                              <LinkBase href={`tel:${option.url}`} underline="none">
                                                                  <TypographyBase variant="paragraph-bold" color="secondary.main">
                                                                      {option?.name}
                                                                  </TypographyBase>
                                                              </LinkBase>
                                                          ) : (
                                                              <TypographyBase
                                                                  variant="paragraph-bold"
                                                                  color={
                                                                      filterDisableMenuOption(option, selectedLoanNumber)
                                                                          ? 'disabled.main'
                                                                          : 'secondary.main'
                                                                  }
                                                              >
                                                                  {option?.name}
                                                              </TypographyBase>
                                                          )}
                                                      </ListItemText>
                                                  </MenuItem>
                                              );
                                          })}
                                      </MenuList>
                                  </ClickAwayListener>
                              </Paper>
                          </Grow>
                      )
                    : undefined}
            </Popper>
        </React.Fragment>
    );
};
